/* ProNorth scroll-lock guard  (+ tippy safety shim)
 * Fixes: mobile page freeze caused by a stuck `lock` class on <html>.
 *
 * Background: the Impact theme's DialogElement adds `lock` to <html>
 * (html.lock { overflow: hidden }) whenever a drawer/modal opens, tracked by a
 * global layer counter. If a dialog ends up [open] while CSS-hidden (breakpoint
 * mismatch, bfcache restore, section re-render race), the lock is never
 * released and the page cannot scroll until a full refresh.
 *
 * This guard removes `lock` ONLY when no visible open dialog exists, so all
 * legitimate drawer/modal scroll-locking keeps working exactly as before.
 * It is passive (MutationObserver + a few cheap event hooks), runs no polling
 * loops, and touches nothing else.
 */

/* ── tippy safety shim ────────────────────────────────────────────────────
 * The Prime badge app (snippets/primeb-js.liquid) loads tippy.js from the
 * unpkg CDN via a dynamically-injected <script>, then calls
 * `tippy('[data-tippy-content]')` inside a FIXED 500ms setTimeout. On cold or
 * slow loads — most visibly the first US visit, when the unpkg cache is cold —
 * the CDN often misses that 500ms window, so `tippy` is still undefined and the
 * call throws `ReferenceError: tippy is not defined`, aborting the rest of that
 * callback (the app's SKU/badge logic).
 *
 * This file is a deferred <head> script, so it runs before that timeout fires.
 * Defining a harmless no-op fallback here means the app's call never throws.
 * When the real tippy.js finishes downloading it reassigns window.tippy (its
 * UMD build overwrites unconditionally), so genuine tooltips still initialize
 * on any later interaction — we only remove the hard crash on the race. */
if (typeof window.tippy === 'undefined') {
  window.tippy = function () { return []; };
}

(function () {
  'use strict';

  var html = document.documentElement;
  var pending = null;

  function isVisible(el) {
    if (!el) return false;
    var st = window.getComputedStyle(el);
    if (st.display === 'none' || st.visibility === 'hidden') return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  /* Impact dialogs are custom elements (cart-drawer, navigation-drawer,
   * search-drawer, quick-buy-drawer, x-popover, modals…) that reflect their
   * state via an [open] attribute. Native <details> never scroll-locks. */
  function hasVisibleOpenDialog() {
    var els = document.querySelectorAll('[open]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].tagName.indexOf('-') > -1 && isVisible(els[i])) return true;
    }
    return false;
  }

  function check() {
    pending = null;
    if (html.classList.contains('lock') && !hasVisibleOpenDialog()) {
      html.classList.remove('lock');
    }
  }

  function schedule(delay) {
    if (pending) return;
    pending = window.setTimeout(check, typeof delay === 'number' ? delay : 300);
  }

  /* React whenever the lock class appears. The 300ms grace period lets
   * legitimate open transitions finish before we evaluate visibility. */
  new MutationObserver(function () { schedule(); })
    .observe(html, { attributes: true, attributeFilter: ['class'] });

  /* iOS/Android back-forward cache restores can resurrect a stale lock. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(50); });
  document.addEventListener('visibilitychange', function () { schedule(); });

  /* Last line of defence: the moment the user touches a frozen page, free it. */
  window.addEventListener('touchstart', function () { schedule(0); }, { passive: true });

  /* Shopify theme editor re-renders sections; re-evaluate afterwards. */
  document.addEventListener('shopify:section:load', function () { schedule(); });
  document.addEventListener('shopify:section:unload', function () { schedule(); });

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', function () { schedule(); });
})();
